import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";
import i18next from "translations/i18nConfig";
import {
  AuthData,
  clearAuthData,
  getAuthData,
  setAuthData,
} from "utils/authData";
import { currentPartner } from "utils/currentPartner";
import { ApiError } from "./ApiError";
import { Page, readPage } from "./page";

// The one HTTP transport behind every /client_api call: host routing, auth
// headers, the session-expiry redirect, timeouts, and the mapping of every
// failure to an ApiError. Two adapters sit on it: `http` below hands callers
// plain data, utils/client.js wraps the same responses in Immutable for the
// redux ducks.

// Every request that gates the UI (sign-in, the boot profile fetch, page
// data) would otherwise wait forever on a stalled connection. Uploads opt out
// per call with `timeout: 0` (UPLOAD_REQUEST_OPTIONS).
export const REQUEST_TIMEOUT_MS = 30_000;

// Multipart uploads run as long as the file takes.
export const UPLOAD_REQUEST_OPTIONS = { timeout: 0 };

export type RequestOptions = {
  signal?: AbortSignal;
  timeout?: number;
  params?: Record<string, unknown>;
};

// Hostnames served by the Yandex VPS. Their API calls go through eks2, the
// nginx pass-through on that VPS. Every other production host talks to eks
// directly — a host missing from this list must never end up on the relay.
const VPS_HOSTS = [
  "app2.sabologistics.com",
  "23industrial.com",
  "www.23industrial.com",
];

const getAxiosParams = (): AxiosRequestConfig => {
  if (currentPartner.api?.baseUrl) {
    return { baseURL: currentPartner.api.baseUrl };
  }

  const { origin } = window.location;
  // development — use dev-server origin so /client_api is same-origin (see server.proxy in vite.config.ts)
  if (origin.includes("localhost"))
    return {
      baseURL: `${origin}/client_api`,
    };

  // staging
  if (
    origin.includes("192.168") ||
    origin.includes("staging") ||
    origin.includes("beta-app") ||
    origin.includes("brocoders.sklad")
  )
    return {
      baseURL: "https://eks.sabollc.com/client_api",
      headers: {
        Authorization: "Basic c2tsYWQ6b3p6a2xs",
      },
    };
  // production
  const { hostname } = window.location;
  let prodHost = "eks.tapegun.me";

  if (hostname === "oae.sabologistics.com") {
    prodHost = "oae-api.tapegun.me";
  } else if (VPS_HOSTS.includes(hostname)) {
    prodHost = "eks2.tapegun.me";
  }

  return {
    baseURL: `https://${prodHost}/client_api`,
    headers: {
      Authorization: "Basic c2tsYWQ6T29wZXF6ejEK",
    },
  };
};

export const instance = axios.create({
  ...getAxiosParams(),
  timeout: REQUEST_TIMEOUT_MS,
  // Report a timeout as ETIMEDOUT instead of sharing ECONNABORTED with aborts.
  transitional: { clarifyTimeoutError: true },
});

instance.interceptors.request.use((request) => {
  Object.assign(request.headers, getAuthData());
  return request;
});

const requestUrl = (response: AxiosResponse) =>
  (response.request as { responseURL?: string } | undefined)?.responseURL ??
  response.config.url ??
  "";

export const toApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) return error;
  // Not from the adapter: an interceptor or a caller threw before the request
  // went out. Reported as its own kind so nothing retries a bug.
  if (!axios.isAxiosError(error)) {
    return new ApiError({
      kind: "unknown",
      message: error instanceof Error ? error.message : String(error),
    });
  }

  const { response, message, code } = error as AxiosError;
  if (!response) {
    if (axios.isCancel(error)) {
      return new ApiError({ kind: "aborted", message: "Request aborted" });
    }
    if (code === "ETIMEDOUT") {
      return new ApiError({
        kind: "timeout",
        message: i18next.t("error.requestTimeout", { ns: "common" }),
      });
    }
    return new ApiError({
      kind: "network",
      message: `Network error: ${message || "No response from server"}`,
    });
  }

  return new ApiError({
    kind: "http",
    status: response.status,
    data: response.data,
    message,
  });
};

// /client_api answers JSON. An HTML document in a 2xx means the request never
// reached the API (a static server's SPA fallback, a captive proxy), so it is
// rejected here rather than handed to a caller as its data.
const isHtmlDocument = (data: unknown) =>
  typeof data === "string" && /^\s*<(!doctype\s+html|html)[\s>]/i.test(data);

instance.interceptors.response.use(
  (response) => {
    const { headers } = response;

    if (requestUrl(response).includes("/auth") && headers["access-token"]) {
      setAuthData(headers as Partial<AuthData>);
    }

    if (isHtmlDocument(response.data)) {
      return Promise.reject(
        new ApiError({
          kind: "unknown",
          data: response.data,
          message: `Unexpected HTML response from ${response.config.url ?? "the API"}`,
        }),
      );
    }

    return response;
  },
  (error: unknown) => {
    const response = axios.isAxiosError(error) ? error.response : undefined;
    const { pathname } = window.location;

    if (response) {
      if (
        response.status === 401 &&
        !(pathname.includes("signin") || pathname.includes("signup"))
      )
        // session expired mid-use — hard navigation also resets the redux store
        clearAuthData().finally(() => window.location.replace("/signin"));

      if (response.headers && response.headers["access-token"])
        setAuthData(response.headers as Partial<AuthData>);
    }

    return Promise.reject(toApiError(error));
  },
);

export const getServerOrigin = () =>
  (instance.defaults.baseURL ?? "").replace("/client_api", "");

const data = <T>(response: AxiosResponse<T>): T => response.data;

// Plain-data adapter: each call resolves with the body (or a Page for lists)
// and rejects with an ApiError.
export const http = {
  get: <T>(url: string, options?: RequestOptions): Promise<T> =>
    instance.get<T>(url, options).then(data),
  post: <T>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => instance.post<T>(url, body, options).then(data),
  patch: <T>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<T> => instance.patch<T>(url, body, options).then(data),
  put: <T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    instance.put<T>(url, body, options).then(data),
  delete: <T>(url: string, options?: RequestOptions): Promise<T> =>
    instance.delete<T>(url, options).then(data),
  page: <T>(url: string, options?: RequestOptions): Promise<Page<T>> =>
    instance.get<T[]>(url, options).then(readPage),
};
